import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { makeRequest } from 'Douleutaras/services/apiClient';
import normalize from 'json-api-normalizer';
import { Dictionary } from 'lodash';
import { RootState } from 'Douleutaras/redux/store';
import { JSONApiDataObject, Notification, NotificationType, ReduxEntity } from 'Douleutaras/types';
import { deepMergeMetaData, generateMetaObj } from 'Douleutaras/utilities/redux/merge';

export const notificationEndpoint = 'api/public/v3/notifications/';
export const singleNotificationEndpoint = id => `${notificationEndpoint}${id}/`;

const notificationsAdapter = createEntityAdapter<JSONApiDataObject<Notification>>({
  selectId: notification => notification.id
});

export const getNotifications = createAsyncThunk(
  'notifications/get/all',
  async ({ page = 1, params = {} }: Partial<{ page: number; params: Dictionary<string> }>, api) => {

    if (page > 1 && typeof window !== 'undefined') {
      // send to gtm that we have scroll inside to new page
      window.postMessage('NOTIFICATION_SCROLL', window.origin || window.location.origin);
    }
    const response = await makeRequest(
      notificationEndpoint,
      {
        method: 'GET',
        additionalGetParams: {
          sort: '-created',
          'page[size]': 10,
          'page[number]': page,
          ...params
        }
      }
    )(api);

    return normalize(response.data, { endpoint: notificationEndpoint });
  }
);

export const getNotificationsStats = createAsyncThunk(
  'notifications/stats',
  async (additionalGetParams: Dictionary<string>, api) => {
    const response = await makeRequest(
      notificationEndpoint,
      {
        method: 'OPTIONS',
        additionalGetParams,
        includes: []
      }
    )(api);

    return { meta: {
      [notificationEndpoint]: generateMetaObj(undefined, undefined, {
        unreadCount: response.data.data.count
      })
    }};
  }
);

export const markNotificationAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async ({ notificationId }: { notificationId: string }, api) => {
    const { setNotificationAsRead } = notificationsSlice.actions;
    const response = await makeRequest(singleNotificationEndpoint(notificationId), {
      method: 'PATCH',
      body: {
        data: {
          type: 'notifications',
          id: notificationId,
          attributes: {
            isRead: true
          }
        }
      }
    })(api);

    api.dispatch(setNotificationAsRead({ notificationId }));
  }
);

export const getUnreadDailyWeeklyNotifications = createAsyncThunk(
  'notifications/get/unreadDailyWeekly',
  async (args, api) => {
    const response = await makeRequest(
      notificationEndpoint,
      {
        method: 'GET',
        additionalGetParams: {
          sort: '-created',
          'page[size]': 10,
          'page[number]': 1,
          'filter[is_new]': 'true',
          'filter[is_read]': 'false',
          'filter[notification_type.in]': `${NotificationType.DAILY}, ${NotificationType.WEEKLY}`
        }
      }
    )(api);

    return response.data;
  }
);

export const notificationsSlice = createSlice({
  name: 'notifications',
  initialState: notificationsAdapter.getInitialState({
    loading: false,
    error: false,
    meta: {},
    entities: {}
  } as ReduxEntity<JSONApiDataObject<Notification>>),
  reducers: {
    setNotificationAsRead: (state, action: { payload: { notificationId: string } }) => {
      state.entities[action.payload.notificationId].attributes.isRead = true;
    }
  },
  extraReducers: builder => {
    builder.addCase(getNotifications.fulfilled, (state, action) => {
      notificationsAdapter.upsertMany(state, action.payload.notifications || {});
      state = deepMergeMetaData(state, action, notificationEndpoint);
      state.loading = false;
      state.error = false;
    });
    builder.addCase(getNotifications.pending, (state, action) => {
      state.loading = true;
      state.error = false;

    });
    builder.addCase(getNotifications.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
    });
    builder.addCase(getNotificationsStats.fulfilled, (state, action) => {
      if (action.payload) {
        state = deepMergeMetaData(state, action, notificationEndpoint);
      }
    });
  }
});

export const notificationsSelectors = notificationsAdapter.getSelectors((state: RootState) => state.notifications);
export default notificationsSlice.reducer;
